/**
 * The server's price for the booking as the draft has it, for the pages after page 2 (batch-29, item R11; plan §6):
 * the rooms the guest chose there and the extras picked on page 3. It is the same question page 2 asks, under the same
 * key, so the figure a guest left page 2 with is the figure page 3 opens on; it is asked again after every change, and
 * while a new answer is on its way the last one stays on screen, dimmed. Every figure is the server's.
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { bookingPrice, type BookingPrice, type BookingRequest, type PublicEvent } from '../public/api';
import { fitExtras } from './extras';
import { bookingBase, bookingRooms, hotelOf, hotelRooms, roomsDone, sharedSuffix, shownRoomLabel, summaryOf } from './rooms';
import { peopleOf, stayOf, stayRulesOf, type Draft } from './state';
import type { BookingSummary } from './Summary';

export const ROOMS_CHANGED = "These rooms can't be booked as they are now. Please go back and choose again.";
/**
 * What a refused price says. Booking that has not opened, or has closed (item PK5), and a party with nobody of the oldest
 * group (0122) are said in the server's own words: choosing other rooms would not help.
 */
export function refusedLine(data: { room_error?: string | null; room_message?: string | null } | null | undefined): string | null {
  if (!data?.room_error) return null;
  if (data.room_error === 'not_open' || data.room_error === 'closed' || data.room_error === 'adult_required') return data.room_message?.trim() || ROOMS_CHANGED;
  // G3 (0165): a place in a shared room refused — the server's own sentence says why
  if (data.room_error.startsWith('share_')) return data.room_message?.trim() || ROOMS_CHANGED;
  return ROOMS_CHANGED;
}

export interface BookingPriceState {
  /** The last price the server gave, as the summary shows it; null before the first. */
  summary: BookingSummary | null;
  /** True while the figures on screen are not the price of what the page shows. */
  pending: boolean;
  /** The sentence to show when the server will not price these rooms any more (one filled up meanwhile). */
  refused: string | null;
  /** The price could not be fetched. */
  failed: boolean;
  retry: () => void;
  /** The server's last answer for exactly what the page shows — null while one is on its way, and when it was a refusal. */
  quote: BookingPrice | null;
  /** What that answer was asked with: the booking, the promo code and the deposit choice. A booking is sent as it was priced. */
  request: Omit<BookingRequest, 'v' | 'mode'> | null;
  coupon: string | null;
  payDeposit: boolean;
  /** Page 5 only: what paying the deposit would charge today — the server's figure; null when the event offers none. */
  depositCents: number | null;
}

/** The promo code as it is sent: trimmed, or nothing. */
export const promoOf = (draft: Pick<Draft, 'promo'>): string | null => (draft.promo ?? '').trim() || null;

export function useBookingPrice(event: PublicEvent, refKey: string, draft: Draft, on: boolean, paying = false): BookingPriceState {
  const rules = useMemo(() => stayRulesOf(event), [event]);
  const groups = useMemo(() => peopleOf(event).groups, [event]);
  const stay = stayOf(rules, draft);
  const hotel = hotelOf(event, draft.hotelId);
  const h = useMemo(() => hotelRooms(event, hotel, { arrive: stay.arrive, leave: stay.leave }), [event, hotel, stay.arrive, stay.leave]);
  const cribKey = groups.find((g) => g.crib)?.key ?? null;
  const rooms = useMemo(() => draft.rooms ?? [], [draft.rooms]);
  const ask = on && roomsDone(event, draft);
  const extras = fitExtras(draft.extras, event.reservation_items, groups, draft.groups, rooms.length).extras;
  const request = ask ? { ...bookingBase(event, rules, draft, stay, hotel?.id ?? null), rooms: bookingRooms(rooms, h, cribKey), ...(extras.length ? { extras } : {}) } : null;
  const requestKey = JSON.stringify(request);
  // a promo code or the deposit choice (page 5) makes it another question; without either it is page 2's own, under page 2's key
  const coupon = promoOf(draft);
  const payDeposit = draft.payDeposit === true;
  const pay = coupon || payDeposit ? { coupon, payDeposit } : null;
  const q = useQuery({
    queryKey: ['book-price', refKey, pay ? `${requestKey}|${JSON.stringify(pay)}` : requestKey],
    queryFn: () => bookingPrice(refKey, request!, pay),
    enabled: ask, staleTime: 0, retry: 1, refetchOnWindowFocus: false, placeholderData: keepPreviousData,
  });
  // page 5, an event that takes deposits: the same booking priced with the deposit chosen, for the amount the choice names
  const asDeposit = { coupon, payDeposit: true };
  const dq = useQuery({
    queryKey: ['book-price', refKey, `${requestKey}|${JSON.stringify(asDeposit)}`],
    queryFn: () => bookingPrice(refKey, request!, asDeposit),
    enabled: ask && paying && event.deposit_enabled === true, staleTime: 0, retry: 1, refetchOnWindowFocus: false, placeholderData: keepPreviousData,
  });
  const data = ask ? q.data : undefined;
  const currency = event.currency || 'USD';
  const summary = useMemo(() => {
    if (!data || data.room_error) return null;
    const s = summaryOf(data, rooms, groups, currency, event.people_per_room);
    // a room picked by its group is named by the group, here as on page 2
    return s && { ...s, rooms: s.rooms.map((r, i) => (rooms[i]?.group ? { ...r, name: `Room ${i + 1} · ${shownRoomLabel(h, rooms[i])}${sharedSuffix(rooms[i], event.people_per_room)}` } : r)) };
  }, [data, rooms, groups, currency, h, event.people_per_room]);
  const settled = Boolean(data) && !q.isFetching && !q.isPlaceholderData;
  return {
    summary,
    pending: ask && (!settled || !summary),
    refused: settled ? refusedLine(data) : null,
    failed: ask && q.isError,
    retry: () => { void q.refetch(); void dq.refetch(); },
    quote: settled && data && !data.room_error && typeof data.total_cents === 'number' ? data : null,
    request, coupon, payDeposit,
    depositCents: ask && paying && dq.data && !dq.isPlaceholderData && !dq.data.room_error && dq.data.deposit_available && typeof dq.data.charge_cents === 'number' ? dq.data.charge_cents : null,
  };
}
